import { getConfig } from '../core/config';

export interface NetworkEntry {
  method: string;
  url: string;
  status: number;
  duration: number;
  requestHeaders: Record<string, string>;
  responseHeaders: Record<string, string>;
  responseSize: number | null;
  error?: string;
  timestamp: string;
}

const buffer: NetworkEntry[] = [];
let intercepting = false;

const originalFetch = window.fetch.bind(window);
const originalXHROpen = XMLHttpRequest.prototype.open;
const originalXHRSend = XMLHttpRequest.prototype.send;
const originalXHRSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

function addEntry(entry: NetworkEntry): void {
  buffer.push(entry);
  const max = getConfig().maxNetworkRequests;
  while (buffer.length > max) buffer.shift();
}

function parseHeaders(headers: Headers): Record<string, string> {
  const result: Record<string, string> = {};
  headers.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}

export function startNetworkInterceptor(): void {
  if (intercepting) return;
  intercepting = true;

  // Patch fetch
  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const method = init?.method?.toUpperCase() || 'GET';
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const start = performance.now();
    const timestamp = new Date().toISOString();
    const reqHeaders: Record<string, string> = {};
    if (init?.headers) {
      const h = new Headers(init.headers);
      h.forEach((v, k) => { reqHeaders[k] = v; });
    }

    try {
      const response = await originalFetch(input, init);
      const duration = Math.round(performance.now() - start);
      const contentLength = response.headers.get('content-length');
      addEntry({
        method,
        url,
        status: response.status,
        duration,
        requestHeaders: reqHeaders,
        responseHeaders: parseHeaders(response.headers),
        responseSize: contentLength ? parseInt(contentLength) : null,
        timestamp,
      });
      return response;
    } catch (err) {
      const duration = Math.round(performance.now() - start);
      addEntry({
        method,
        url,
        status: 0,
        duration,
        requestHeaders: reqHeaders,
        responseHeaders: {},
        responseSize: null,
        error: err instanceof Error ? err.message : String(err),
        timestamp,
      });
      throw err;
    }
  };

  // Patch XHR
  XMLHttpRequest.prototype.open = function (method: string, url: string | URL, ...rest: unknown[]) {
    (this as XMLHttpRequest & { _brMeta: Record<string, unknown> })._brMeta = {
      method: method.toUpperCase(),
      url: typeof url === 'string' ? url : url.href,
      reqHeaders: {} as Record<string, string>,
      start: 0,
      timestamp: '',
    };
    return originalXHROpen.apply(this, [method, url, ...rest] as Parameters<typeof originalXHROpen>);
  };

  XMLHttpRequest.prototype.setRequestHeader = function (name: string, value: string) {
    const meta = (this as XMLHttpRequest & { _brMeta: Record<string, unknown> })._brMeta;
    if (meta) {
      (meta.reqHeaders as Record<string, string>)[name] = value;
    }
    return originalXHRSetRequestHeader.call(this, name, value);
  };

  XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
    const meta = (this as XMLHttpRequest & { _brMeta: Record<string, unknown> })._brMeta;
    if (meta) {
      meta.start = performance.now();
      meta.timestamp = new Date().toISOString();
    }

    this.addEventListener('loadend', () => {
      if (!meta) return;
      const duration = Math.round(performance.now() - (meta.start as number));
      const respHeaders: Record<string, string> = {};
      const allHeaders = this.getAllResponseHeaders();
      if (allHeaders) {
        allHeaders.split('\r\n').forEach(line => {
          const parts = line.split(': ');
          if (parts.length === 2) {
            respHeaders[parts[0]] = parts[1];
          }
        });
      }
      const contentLength = this.getResponseHeader('content-length');
      addEntry({
        method: meta.method as string,
        url: meta.url as string,
        status: this.status,
        duration,
        requestHeaders: meta.reqHeaders as Record<string, string>,
        responseHeaders: respHeaders,
        responseSize: contentLength ? parseInt(contentLength) : null,
        error: this.status === 0 ? 'Network error' : undefined,
        timestamp: meta.timestamp as string,
      });
    });

    return originalXHRSend.call(this, body);
  };
}

export function stopNetworkInterceptor(): void {
  if (!intercepting) return;
  intercepting = false;
  window.fetch = originalFetch;
  XMLHttpRequest.prototype.open = originalXHROpen;
  XMLHttpRequest.prototype.send = originalXHRSend;
  XMLHttpRequest.prototype.setRequestHeader = originalXHRSetRequestHeader;
}

export function getNetworkRequests(): NetworkEntry[] {
  return [...buffer];
}
